import { useEffect, useRef, useState } from 'react';
import 'katex/dist/katex.min.css';
import { FractalCanvas } from './FractalCanvas';
import { FractalSubsection } from './FractalSubsection';
import { JacobianUnskewExample, UNSKEW_EXAMPLES } from './JacobianUnskewExample';
import {
  abyssalZoomPresets,
  chthonicZoomPresets,
  deepZoomPresets,
  overviewPresets,
  substructurePresets,
} from './substructurePresets';
import { fireColor } from './firePalette';
import { nebulabrotColor } from './fractal_math';
import { codeSnippets, equations } from './staticContent';
import { renderedCode, renderedEquations } from './staticContent.generated';

const substructureSources = [...new Map(
  substructurePresets
    .filter(({ source, sourceLabel }) => source && sourceLabel)
    .map(({ source, sourceLabel }) => [source, sourceLabel])
)];

export const PAGE_SECTIONS = [
  { id: 'intro', label: 'Introduction' },
  { id: 'world-overview', label: 'World overview' },
  { id: 'equation', label: 'Fractal equation' },
  {
    id: 'substructures',
    label: 'Sub-structures',
    subsections: [
      { id: 'substructures-overview', label: 'Overview' },
      { id: 'substructures-deep-zoom', label: 'Deep zoom' },
      { id: 'substructures-abyssal-zoom', label: 'Abyssal zoom' },
      { id: 'substructures-chthonic-zoom', label: 'Chthonic zoom' },
    ],
  },
  {
    id: 'coloring',
    label: 'Fractal coloring',
    subsections: [
      { id: 'coloring-escape-time', label: 'Escape time' },
      { id: 'coloring-nebulabrot', label: 'Nebulabrot' },
    ],
  },
  { id: 'rendering', label: 'Fractal rendering' },
  { id: 'unskewing', label: 'Jacobian skew' },
  { id: 'about', label: 'About' },
];

const iterationOnlyColor = ({ numIterations }) => [
  255,
  numIterations * 7,
  0,
  numIterations * 15,
];

// KaTeX and highlight.js run in scripts/generate-static-content.mjs, so the
// article ships as pre-rendered HTML and neither library reaches the browser.
const Equation = ({ name }) => {
  const html = renderedEquations[name];
  if (html === undefined) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`Missing pre-rendered equation "${name}". Run \`yarn generate:content\`.`);
    }
    return <div className="equation" data-nosnippet="">{equations[name] ?? name}</div>;
  }
  return <div className="equation" data-nosnippet="" dangerouslySetInnerHTML={{ __html: html }} />;
};

const CodeBlock = ({ name }) => {
  const html = renderedCode[name];
  if (html === undefined) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`Missing pre-rendered code snippet "${name}". Run \`yarn generate:content\`.`);
    }
    return <pre data-nosnippet=""><code className="language-javascript hljs">{codeSnippets[name] ?? name}</code></pre>;
  }
  return (
    <pre data-nosnippet="">
      <code className="language-javascript hljs" dangerouslySetInnerHTML={{ __html: html }} />
    </pre>
  );
};

const SidebarNav = ({ headerRef }) => {
  const [activeSection, setActiveSection] = useState(PAGE_SECTIONS[0].id);
  const [sidebarStyle, setSidebarStyle] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    if (globalThis.__PRERENDER__) return undefined;
    let scrollFrame = null;
    const selectSection = nextId => setActiveSection(prev => (prev === nextId ? prev : nextId));

    const updateHeaderOffset = () => {
      const headerEl = headerRef.current;
      if (!headerEl) return;
      const headerRect = headerEl.getBoundingClientRect();
      if (headerRect.bottom > 0 || headerRect.height > 0) {
        const anchorTop = Math.max(32, Math.round((headerEl.offsetTop || 0) + headerRect.height + 17));
        const isFixed = headerRect.bottom + 17 <= 32;
        const nextPosition = isFixed ? 'fixed' : 'absolute';
        const nextTop = `${isFixed ? 32 : anchorTop}px`;
        if (navRef.current) {
          if (navRef.current.style.position !== nextPosition) {
            navRef.current.style.position = nextPosition;
          }
          if (navRef.current.style.top !== nextTop) {
            navRef.current.style.top = nextTop;
          }
        }
        setSidebarStyle(prev => (
          prev && prev.position === nextPosition && prev.top === nextTop
            ? prev
            : { position: nextPosition, top: nextTop }
        ));
      }
    };

    const updateSidebarState = () => {
      scrollFrame = null;
      if (document.body?.classList.contains('fullscreen-active')) return;
      updateHeaderOffset();

      const rects = PAGE_SECTIONS.map(section => {
        const el = document.getElementById(section.id);
        return el ? { section, rect: el.getBoundingClientRect() } : null;
      }).filter(Boolean);

      if (rects.length === 0) return;

      const hasLayout = rects.some(({ rect }) =>
        rect.top !== 0 || rect.bottom !== 0 || rect.height !== 0
      );
      if (!hasLayout) return;

      const viewportHeight = window.innerHeight || 800;
      const docHeight = document.documentElement?.scrollHeight || 0;
      if (window.scrollY > 0 && docHeight > viewportHeight &&
          window.scrollY + viewportHeight >= docHeight - 24) {
        selectSection(PAGE_SECTIONS[PAGE_SECTIONS.length - 1].id);
        return;
      }

      const threshold = Math.max(120, viewportHeight * 0.28);
      let currentSection = PAGE_SECTIONS[0];
      for (const { section, rect } of rects) {
        if (rect.top <= threshold) {
          currentSection = section;
        }
      }

      if (currentSection.subsections?.length) {
        let currentSubId = currentSection.subsections[0].id;
        for (const sub of currentSection.subsections) {
          const subEl = document.getElementById(sub.id);
          if (!subEl) continue;
          const subRect = subEl.getBoundingClientRect();
          const subHasLayout = subRect.top !== 0 || subRect.bottom !== 0 || subRect.height !== 0;
          if (subHasLayout && subRect.top <= threshold) {
            currentSubId = sub.id;
          }
        }
        selectSection(currentSubId);
        return;
      }

      selectSection(currentSection.id);
    };

    const handleScrollOrResize = () => {
      if (globalThis.IS_REACT_ACT_ENVIRONMENT || typeof requestAnimationFrame !== 'function') {
        updateSidebarState();
        return;
      }
      updateHeaderOffset();
      if (scrollFrame === null) {
        scrollFrame = requestAnimationFrame(updateSidebarState);
      }
    };

    updateSidebarState();
    window.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      if (scrollFrame !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(scrollFrame);
      }
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [headerRef]);

  return (
    <nav
      ref={navRef}
      className="sidebar-nav"
      aria-label="Page sections"
      data-nosnippet=""
      style={sidebarStyle ?? undefined}
    >
      <ul className="sidebar-nav-list">
        {PAGE_SECTIONS.map(({ id, label, subsections }) => {
          const hasActiveChild = Boolean(subsections?.some(sub => sub.id === activeSection));
          const isActive = activeSection === id;
          return (
            <li key={id} className="sidebar-nav-item">
              <a
                href={`#${id}`}
                className={`sidebar-nav-link${isActive ? ' active' : ''}${hasActiveChild ? ' active-parent' : ''}`}
                aria-current={isActive ? 'location' : undefined}
                onClick={() => setActiveSection(subsections?.length ? subsections[0].id : id)}
              >
                {label}
              </a>
              {subsections?.length > 0 && (
                <ul className="sidebar-nav-sublist">
                  {subsections.map(sub => {
                    const isSubActive = activeSection === sub.id;
                    return (
                      <li key={sub.id} className="sidebar-nav-subitem">
                        <a
                          href={`#${sub.id}`}
                          className={`sidebar-nav-link sidebar-nav-sublink${isSubActive ? ' active' : ''}`}
                          aria-current={isSubActive ? 'location' : undefined}
                          onClick={() => setActiveSection(sub.id)}
                        >
                          {sub.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

const App = () => {
  const preferredRenderer = 'webgl';
  const headerRef = useRef(null);

  return <>
  <header ref={headerRef}>
    <h1>Burning ship fractal</h1>
    <FractalCanvas
      width={4000}
      height={1000}
      xRange={[-1.95, -1.45]}
      yRange={[-0.09, 0.02]}
      colorFunc={fireColor}
      preferredRenderer={preferredRenderer}
      showRendererToggle={true}
      samplesPerPixel={1}
      fallbackSrc="./images/burning-ship-armada-banner.webp"
      fallbackAlt="Panoramic view of the Burning Ship fractal armada along the negative real axis from x = -1.95 to -1.45"
      fetchPriority="high"
    />
  </header>

  <SidebarNav headerRef={headerRef} />

  <main>
  <section id="intro" className="section" data-section="intro">
  <div className="container">
    <p>
      If we plot the escape times for points in this complex plane series,
      we end up in a world of burning ships.
    </p>
    <Equation name="burning-ship-with-i" />
    <p>
      The largest ship is located on the real axis at -1.75.
    </p>
    <FractalCanvas
      width={1600}
      height={1600}
      xRange={[-1.8, -1.7]}
      yRange={[-0.08, 0.01]}
      colorFunc={fireColor}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/largest-burning-ship.webp"
      fallbackAlt="The largest ship in the Burning Ship fractal centered on the real axis near x = -1.75"
    />
    <p className="deep-zoom-instructions" data-nosnippet="">
      <span className="control-hint"><span className="controls-trigger">drag</span> to pan</span>
      <span className="control-separator" aria-hidden="true">·</span>
      <span className="control-hint"><span className="controls-trigger">shift + scroll</span> or <span className="controls-trigger">pinch</span> to zoom</span>
    </p>
  </div>
  </section>

  <section id="world-overview" className="section" data-section="world-overview">
  <div className="container">
    <h2>World overview</h2>
    <p>
      If we consider an escape radius of 2, the world also has a radius
      of 2, since any points starting outside of the world diverge right away.
    </p>
    <FractalCanvas
      width={800}
      height={800}
      xRange={[-2.2, 2.2]}
      yRange={[-2.2, 2.2]}
      colorFunc={fireColor}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/world-escape-radius.webp"
      fallbackAlt="Full Burning Ship fractal world overview within the escape radius of 2 from -2.2 to 2.2"
    />

    <p>
      The ships are located near the real axis (y = 0) among negative
      real numbers.
    </p>
    <FractalCanvas
      width={800}
      height={400}
      xRange={[-2.5, 0.5]}
      yRange={[-1, 0.5]}
      colorFunc={fireColor}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/world-real-axis.webp"
      fallbackAlt="Burning Ship fractal overview showing the ships clustered near the negative real axis (y = 0)"
    />

    <p>Zooming in closer to the ships in the bottom left</p>
    <FractalCanvas
      width={800}
      height={600}
      xRange={[-1.81, -1.39]}
      yRange={[-0.32, 0.03]}
      colorFunc={fireColor}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/world-bottom-left-ships.webp"
      fallbackAlt="Zoomed-in view of the Burning Ship fleet in the lower-left region from x = -1.81 to -1.39"
    />

    <p>The ship near -1.6 between the largest ship and the dark expanse.</p>
    <FractalCanvas
      width={800}
      height={800}
      xRange={[-1.65, -1.58]}
      yRange={[-0.074, 0.018]}
      colorFunc={fireColor}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/ship-near-minus-1-6.webp"
      fallbackAlt="Burning Ship structure near x = -1.6 between the largest ship and the dark interior expanse"
    />

    <p>Small ships near the edge of darkness</p>
    <FractalCanvas
      width={800}
      height={400}
      xRange={[-1.59, -1.49]}
      yRange={[-0.052, 0.014]}
      colorFunc={fireColor}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/small-ships-near-edge.webp"
      fallbackAlt="Chain of small ships along the real axis between x = -1.59 and -1.49 near the dark expanse"
    />

    <p>Zoomed in to the small ship near -1.57</p>
    <FractalCanvas
      width={1600}
      height={1600}
      xRange={[-1.5805, -1.55]}
      yRange={[-0.04, 0.006]}
      colorFunc={fireColor}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/small-ship-minus-1-57.webp"
      fallbackAlt="Close-up of the small Burning Ship structure near x = -1.57 with tall lacy masts"
    />

    <p>Small ship and disorder near the expanse, with curving spires.</p>
    <FractalCanvas
      width={1600}
      height={1600}
      xRange={[-1.529, -1.499]}
      yRange={[-0.06, 0.005]}
      colorFunc={fireColor}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/curving-spires-minus-1-51.webp"
      fallbackAlt="Small Burning Ship and curving flame spires near the edge of the dark expanse around x = -1.51"
    />

    <p>
      Left of the largest ship are some very small ships. The light ends
      at (-2, 0)
    </p>
    <FractalCanvas
      width={800}
      height={600}
      xRange={[-2.04, -1.66]}
      yRange={[-0.142, 0.072]}
      colorFunc={fireColor}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/western-antenna-ships.webp"
      fallbackAlt="Small ships along the western needle left of the largest ship extending to (-2, 0)"
    />

    <p>Zoomed in to a small ship near -1.94</p>
    <FractalCanvas
      width={1600}
      height={1200}
      xRange={[-1.948, -1.925]}
      yRange={[-0.0095,  0.002]}
      colorFunc={fireColor}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/small-ship-minus-1-94.webp"
      fallbackAlt="Zoomed-in view of a small Burning Ship along the western needle near x = -1.94"
    />
  </div>
  </section>

  <section id="equation" className="section" data-section="equation">
  <div className="container">
    <h2>Burning ship fractal equation</h2>

    <p>The equation using complex numbers is this:</p>
    <Equation name="burning-ship-iteration" />

    <p>It's similar to the <a href="https://robotmoon.com/mandelbrot-set/">Mandelbrot equation</a>:</p>
    <Equation name="mandelbrot-iteration" />
    <p>
      Except instead of squaring each number, we add the absolute values of the
      real and imaginary components together during each iteration and square
      the sum instead.
    </p>

    <p>z is a complex number composed of real and imaginary numbers:</p>
    <Equation name="complex-number" />

    <p>Fractal images are generated by changing the value of c:</p>
    <Equation name="complex-parameter" />

    <p>If we convert to using x and y, the burning ship fractal equation reduces to:</p>
    <Equation name="real-part-iteration" />
    <Equation name="imaginary-part-iteration" />

    <section>
      <p>
        A common way of exploring fractals is by putting different starting (x0, y0)
        numbers into the equation and making note of whether the series:
      </p>
      <ul>
        <li>diverges to infinity or stays within a limit</li>
        <li>if the series diverges, we make note of:
          <ul>
            <li>the number of iterations until reaching the escape radius</li>
            <li>the distance from zero (0, 0) during the escape</li>
          </ul>
        </li>
      </ul>
    </section>
  </div>
  </section>

  <section id="substructures" className="section" data-section="substructures">
  <div className="container">
    <h2>Sub-structures</h2>
    <p>
      Beyond the main fleet, the fractal is filled with bridges, spirals,
      lattices, and nested mini-ships. Zooming into the masts and surrounding
      filaments uncovers strange new shapes at every scale.
    </p>

    <div id="substructures-overview" className="substructures-subsection">
      <h3 className="substructures-subsection-title">Overview</h3>
      <p className="substructures-subsection-intro">
        At shallow magnifications, every pixel runs its own independent
        escape-time loop inside a WebGL fragment shader using native 32-bit GPU
        floats.
      </p>

      <p>
        The mast of the largest ship
      </p>
      <FractalCanvas
        width={1600}
        height={1600}
        xRange={[-1.7825, -1.765]}
        yRange={[-0.0671, -0.0494]}
        colorFunc={fireColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/largest-ship-mast.webp"
        fallbackAlt="Intricate branching filaments at the top of the mast of the largest Burning Ship"
      />

      <p>
        Distant view when approaching the expanse along the real axis near x = -1.5
      </p>
      <FractalCanvas
        width={1600}
        height={800}
        xRange={[-1.510987, -1.5090825]}
        yRange={[-0.00296972, 0.00066197]}
        deepColorRange={[17.1, 68]}
        colorFunc={fireColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/expanse-horizon-minus-1-51.webp"
        fallbackAlt="Horizon view approaching the main dark expanse along the real axis near x = -1.51"
      />

      <p>
        The left edge of the world near x = -2.0
      </p>
      <FractalCanvas
        width={1600}
        height={800}
        xRange={[-2.01, -1.9469]}
        yRange={[-0.02, 0.02]}
        colorFunc={fireColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/western-tip-minus-2.webp"
        fallbackAlt="Western tip of the Burning Ship fractal along the real axis near x = -2.0"
      />

      {overviewPresets.map(preset => <FractalSubsection key={preset.id} preset={preset} />)}
    </div>
  </div>

  <div
    id="substructures-deep-zoom"
    className="substructures-subsection substructures-subsection-deep"
    data-section="deep-zoom"
  >
    <div className="container">
      <h3 className="substructures-subsection-title">Deep zoom</h3>
      <p className="substructures-subsection-intro">
        Once the viewport narrows past 10<sup>-7</sup>, single-precision <code>float32</code> shaders
        lose subpixel spacing. These views step up
        to 53-bit significand <code>float64</code> workers, WebAssembly SIMD
        loops, and double-float (<code>df64</code>) WebGPU shaders to keep
        filaments razor-sharp down to the 10<sup>-15</sup> IEEE 754 precision
        floor.
      </p>
      {deepZoomPresets.map(preset => <FractalSubsection key={preset.id} preset={preset} />)}
    </div>
  </div>

  <div
    id="substructures-abyssal-zoom"
    className="substructures-subsection substructures-subsection-abyssal"
    data-section="abyssal-zoom"
  >
    <div className="container">
      <h3 className="substructures-subsection-title">Abyssal zoom</h3>
      <p className="substructures-subsection-intro">
        Down in these abyssal depths past 10<sup>-16</sup>, standard 64-bit
        numbers can no longer tell neighboring pixels apart, and running custom
        big-number math for every pixel would bring the browser to a crawl.
        Instead, the renderer uses perturbation theory: it traces a single
        high-precision reference orbit through the center of the frame and
        measures how surrounding pixels drift from that path, patching any
        seams where the equation&apos;s absolute-value folds crease the
        geometry.
      </p>
      {abyssalZoomPresets.map(preset => <FractalSubsection key={preset.id} preset={preset} />)}
    </div>
  </div>

  <div
    id="substructures-chthonic-zoom"
    className="substructures-subsection substructures-subsection-chthonic"
    data-section="chthonic-zoom"
  >
    <div className="container">
      <h3 className="substructures-subsection-title">Chthonic zoom</h3>
      <p className="substructures-subsection-intro">
        Here we descend impossibly deep, past the resolution limit of the
        physical world - Planck&apos;s constant: 10<sup>-34</sup> - and the 10
        <sup>-38</sup> underflow cliff where 32-bit floats collapse to zero.
        Beyond this limit, the engine pairs WebAssembly <code>libmpdec</code> arbitrary-precision
        decimals with 240-bit
        fixed-point Taylor series and detached-exponent perturbation math to
        keep filaments sharp without underflowing.
      </p>
      {chthonicZoomPresets.map(preset => <FractalSubsection key={preset.id} preset={preset} />)}
    </div>
  </div>
  </section>

  <section id="coloring" className="section" data-section="coloring">
  <div className="container">
    <h2>Fractal coloring</h2>

    <div id="coloring-escape-time" className="coloring-subsection">
      <h3 className="coloring-subsection-title">Escape time</h3>
      <p>
        Given a starting point (x0, y0), we can give each
        point a color by calculating the number of iterations it takes
        for the series to diverge, and the distance from zero when escaping.
      </p>
      <CodeBlock name="iterate-until-escape"/>

      <p>
        We'll set the RGBA colors of every pixel in the image based
        on the number of iterations. Here's an example of a fiery yellow/red
        color palette by scaling just the green and alpha values of each pixel.
        This simple mapping clips to the same yellow above 36 iterations, hiding
        differences in the brightest areas.
      </p>
      <CodeBlock name="iteration-color-palette"/>

      <p>
        Here's a general function for drawing fractals onto canvas elements.
        Changing the iterateUntilEscape function will change the drawn fractal.
      </p>

      <CodeBlock name="draw-fractal-canvas"/>

      <p>
        Here's the largest ship colored based on
        number of iterations alone.
      </p>
      <FractalCanvas
        width={1600}
        height={1600}
        xRange={[-1.8, -1.7]}
        yRange={[-0.08, 0.01]}
        colorFunc={iterationOnlyColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/largest-ship-iteration-coloring.webp"
        fallbackAlt="Largest Burning Ship colored using raw integer iteration counts, showing color banding and clipped yellow highlights"
      />
      <p>
        To eliminate color banding, we can compute a normalized iteration count
        (&mu;) that subtracts a double-logarithmic fraction of how far past the
        escape radius the point landed, turning discrete iteration steps into a
        smooth, continuous gradient.
      </p>
      <CodeBlock name="escape-time-coloring"/>
      <p>
        Here's the same image with smooth escape counts and an expanded yellow
        palette. The original fiery brightness stays intact, while clipped
        highlights spread from saturated yellow through lemon to pale yellow
        and ivory. Colors stay stable as you pan and zoom.
      </p>
      <FractalCanvas
        width={1600}
        height={1600}
        xRange={[-1.8, -1.7]}
        yRange={[-0.08, 0.01]}
        colorFunc={fireColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/largest-burning-ship.webp"
        fallbackAlt="Largest Burning Ship colored with continuous logarithmic escape-time smoothing and an expanded fiery yellow palette"
      />
    </div>
  </div>

  <div
    id="coloring-nebulabrot"
    className="coloring-subsection coloring-subsection-nebulabrot"
    data-section="nebulabrot"
  >
    <div className="container">
      <h3 className="coloring-subsection-title">Nebulabrot</h3>
      <p>
        While escape-time coloring only cares how long a point takes to escape,
        a Nebulabrot tracks every step along the way and brightens pixels that
        get visited the most. Adapted from the Buddhabrot technique for
        the <a href="https://robotmoon.com/mandelbrot-set/">Mandelbrot set</a>, it
        layers three orbit-density histograms by mapping
        fast, medium, and slow-escaping trajectories to red, green, and blue
        color channels.
      </p>
      <p>
        Tracing those paths fills the dark hull of the largest ship with blue
        light and dusty golden sails.
      </p>
      <FractalCanvas
        width={1600}
        height={1600}
        xRange={[-1.8, -1.7]}
        yRange={[-0.08, 0.01]}
        colorFunc={nebulabrotColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/largest-ship-nebulabrot.webp"
        fallbackAlt="Nebulabrot render of the largest Burning Ship colored by orbit visit frequency across escaping trajectories"
      />
      <p>
        Pulling back from the largest ship shows an ethereal line of ships
        along the real axis, leading toward a tall cloud of blue light at the
        edge of the main expanse.
      </p>
      <FractalCanvas
        width={1600}
        height={960}
        xRange={[-1.865, -1.418]}
        yRange={[-0.182, 0.088]}
        colorFunc={nebulabrotColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/fleet-channel-nebulabrot.webp"
        fallbackAlt="Wide Nebulabrot view of the Burning Ship fleet from x = -1.865 to -1.418 showing the largest ship, companion ships, and a glowing cyan prominence at the edge of the main expanse"
      />
      <p>
        Deep inside the main dark expanse near (0, 0), where escape-time
        renders show only empty space, we now see glitchy desert dunes.
      </p>
      <FractalCanvas
        width={1600}
        height={1200}
        xRange={[-0.62, 0.18]}
        yRange={[0.05, 0.65]}
        colorFunc={nebulabrotColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/caustic-chalice-nebulabrot.webp"
        fallbackAlt="Nebulabrot render of layered parabolic caustic folds forming a glowing chalice across the dark interior near the origin"
      />
      <p>
        Further down and to the right inside the dark interior, a row of blades
        curve out of a bright white glow.
      </p>
      <FractalCanvas
        width={1600}
        height={1600}
        xRange={[0.05, 0.47]}
        yRange={[-0.43, -0.01]}
        colorFunc={nebulabrotColor}
        preferredRenderer={preferredRenderer}
        fallbackSrc="./images/scimitar-caustics-nebulabrot.webp"
        fallbackAlt="Portrait Nebulabrot render of curved crimson, amber, and cyan parabolic caustic blades inside the southeastern interior cusp"
      />
    </div>
  </div>
  </section>

  <section id="rendering" className="section" data-section="rendering">
    <div className="container">
      <h2>Fractal rendering</h2>
      <p>
        Zooming deeper into the fractal crosses several hardware number limits.
        Each time a floating-point format runs out of significand bits to
        separate neighboring pixels&mdash;or runs out of exponent bits and
        underflows to zero&mdash;the renderer switches its coordinate
        representation and orbit math to keep the image sharp.
      </p>
      <div className="rendering-table-wrapper">
        <table className="rendering-table">
          <thead>
            <tr>
              <th scope="col">Zoom level</th>
              <th scope="col">What happens</th>
              <th scope="col">How the renderer adapts</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">
                <span className="rendering-stage-name">Overview</span>{' '}
                <span className="rendering-stage-span" data-nosnippet="">10<sup>0</sup> to 10<sup>-6</sup></span>
              </th>
              <td>
                32-bit GPU floats (<code>float32</code>, 24 significand bits)
                easily distinguish neighboring pixels.
              </td>
              <td>
                Runs a direct escape-time loop per pixel in a single-pass WebGL
                fragment shader (255 iterations), with a 2D canvas worker pool
                for fallback and Nebulabrot accumulation.
              </td>
            </tr>
            <tr>
              <th scope="row">
                <span className="rendering-stage-name">Deep zoom</span>{' '}
                <span className="rendering-stage-span" data-nosnippet="">10<sup>-7</sup> to 10<sup>-15</sup></span>
              </th>
              <td>
                <code>float32</code> loses subpixel spacing, quantizing smooth
                curves into staircase blocks.
              </td>
              <td>
                Promotes the camera to decimal strings, scales the iteration
                budget with depth (up to 20,000), and switches to paired-float
                (<code>df64</code>) WebGPU shaders and 64-bit (<code>float64</code>)
                WebAssembly SIMD workers.
              </td>
            </tr>
            <tr>
              <th scope="row">
                <span className="rendering-stage-name">Abyssal zoom</span>{' '}
                <span className="rendering-stage-span" data-nosnippet="">10<sup>-16</sup> to 10<sup>-38</sup></span>
              </th>
              <td>
                64-bit <code>float64</code> (53 significand bits) can no longer
                tell adjacent pixel coordinates apart, though small pixel
                offsets still fit in hardware floats.
              </td>
              <td>
                Traces one high-precision reference orbit through the center of
                the view and evolves per-pixel deltas with perturbation theory
                and 4th-order Taylor series, repairing absolute-value fold seams
                with local anchor orbits and 240-bit fixed-point (<code>fp240</code>)
                WASM.
              </td>
            </tr>
            <tr>
              <th scope="row">
                <span className="rendering-stage-name">Chthonic zoom</span>{' '}
                <span className="rendering-stage-span" data-nosnippet="">10<sup>-38</sup> to 10<sup>-300</sup></span>
              </th>
              <td>
                32-bit floats underflow to zero at 10<sup>-38</sup>, and
                240-bit fixed-point runs out of fractional bits past 10<sup>-45</sup>.
              </td>
              <td>
                Detaches a shared binary exponent (<code>FloatExp</code>) so GPU
                perturbation deltas stay scaled near 1, while reference orbits
                and seam repairs step up to multi-limb fixed-point WASM and
                C <code>libmpdec</code> arbitrary-precision decimals.
              </td>
            </tr>
            <tr>
              <th scope="row">
                <span className="rendering-stage-name">The darkest depths</span>{' '}
                <span className="rendering-stage-span" data-nosnippet="">10<sup>-300</sup> and below</span>
              </th>
              <td>
                64-bit JavaScript <code>Number</code> underflows to zero near
                10<sup>-308</sup>, collapsing standard floating-point pixel
                deltas.
              </td>
              <td>
                Replaces floating-point pixel spacing with rational offsets over
                multi-thousand-digit decimals and extracts rescaled <code>FloatExp</code> mantissas
                straight from fixed-point <code>BigInt</code>s
                alongside <code>libmpdec</code> WASM
                workers.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        To render past 10<sup>-16</sup> in real time, the engine relies on
        perturbation theory: instead of running slow arbitrary-precision math
        for every pixel, it traces a single high-precision reference orbit
        through the center of the view and tracks each pixel&apos;s tiny offset
        from that path in fast hardware floats. When the absolute-value fold
        causes a pixel&apos;s path to split away from the central orbit across
        zero, the renderer adds extra local reference points to keep the image
        free of seams.
      </p>
      <p>
        To keep navigation responsive while deep frames compute, the canvas
        reprojects the previous frame during pan and zoom gestures, draws a
        quick low-resolution preview before filling in full detail, and only
        antialiases sharp edges.
      </p>
    </div>
  </section>

  <section id="unskewing" className="section" data-section="unskewing">
    <div className="container">
      <h2>Jacobian skew</h2>
      <p>
        In the <a href="https://robotmoon.com/mandelbrot-set/">Mandelbrot set</a>,
        each step of the equation scales and rotates space evenly in every
        direction, so deep mini-sets and spirals keep their natural proportions
        at a 1:1 aspect ratio. In the Burning Ship, taking absolute values
        folds the plane across the axes on each step. As an orbit bounces
        through those folds, space gets stretched along one direction and
        squished along the other.
      </p>
      <p>
        Because of this uneven stretching, most deep mini-ships, rings, and
        spirals away from the main axes look squashed into thin diagonal
        streaks. By tracking how much an orbit stretches space in <code>x</code> and <code>y</code> (its
        Jacobian matrix) and applying the opposite stretch to the camera, we
        can unskew the view and reveal the shapes hiding inside the streaks:
      </p>
      <Equation name="jacobian-unskew" />
      {UNSKEW_EXAMPLES.map(example => (
        <JacobianUnskewExample key={example.id} example={example} />
      ))}
    </div>
  </section>

  <section id="about" className="section" data-section="about">
    <div className="container">
      <h2>About</h2>
      <p>
        All fractals on the page are interactive and rendered when
        they're in view. Every image can be expanded into full-screen mode
        using the button in its bottom-right corner. Full-screen views also
        display live complex-plane coordinates and let you undo camera steps or
        reset to the starting view.
      </p>
      <p>
        As you zoom deeper, each canvas automatically switches to more
        sophisticated rendering pipelines to stay fast and avoid pixelation.
        Shallow views run per-pixel escape loops in 32-bit WebGL fragment
        shaders.
      </p>
      <p>
        Zooming past 10<sup>-7</sup> switches to 64-bit WebAssembly workers and
        double-float WebGPU shaders. Beyond 10<sup>-16</sup>, the renderer
        switches to perturbation theory and arbitrary-precision decimal math so
        you can keep zooming without losing detail.
      </p>
      <p data-nosnippet="">Fractal image controls when hovering</p>
      <ul className="controls-list" data-nosnippet="">
        <li>
          <span className="controls-trigger">shift + scroll</span>{' '}
          <span className="controls-action">zoom in/out</span>
        </li>
        <li>
          <span className="controls-trigger">pinch</span>{' '}
          <span className="controls-action">zoom in/out</span>
        </li>
        <li>
          <span className="controls-trigger">click + drag</span>{' '}
          <span className="controls-action">pan the view</span>
        </li>
        <li>
          <span className="controls-trigger">cmd + z</span>{' '}
          <span className="controls-action">undo the last camera action</span>
        </li>
        <li>
          <span className="controls-trigger">fullscreen button / esc</span>{' '}
          <span className="controls-action">enter or exit full-screen mode</span>
        </li>
        <li>
          <span className="controls-trigger">cmd + \</span>{' '}
          <span className="controls-action">toggle zen mode in fullscreen</span>
        </li>
      </ul>

      <p>Source code</p>
      <ul>
        <li>
          <a href="https://github.com/linrock/burning-ship-fractal">
            https://github.com/linrock/burning-ship-fractal
          </a>
        </li>
      </ul>
    </div>
  </section>
  </main>

  <footer>
    <div className="container">
      <h2>References</h2>
      <ul>
        <li><a href="https://en.wikipedia.org/wiki/Burning_Ship_fractal">Wikipedia - Burning ship fractal</a></li>
        <li><a href="https://mathr.co.uk/helm/AtTheHelmOfTheBurningShip-Paper.pdf">Claude Heiland-Allen - At the Helm of the Burning Ship (PDF)</a></li>
        {substructureSources.map(([source, sourceLabel]) => (
          <li key={source}><a href={source}>{sourceLabel}</a></li>
        ))}
      </ul>
    </div>
    <div className="footer-home">
      <div className="container">
        <a href="https://robotmoon.com/">Home</a>
      </div>
    </div>
  </footer>
</>;
};

export default App;
